<template>
  <div class="d-flex flex-column gap-1 position-relative">
    <LookupBar
      v-if="resolve"
      @searchcompleted="applyLookupResult"
    />

    <div>
      <div
        v-if="disabled"
        class="disabled"
      />
      <iframe
        ref="ketcherFrame"
        class="ketcher-frame"
        src="/ketcher/ketcher.html"
        title="Ketcher editor"
        @load="loadFrame"
      />
    </div>

    <v-card
      v-if="showFound && record && (record.name || record.casrn || record.sid || record.smiles)"
      tile
      outlined
      :elevation="1"
    >
      <v-card-text class="py-1">
        <div class="d-flex flex-row align-items-center justify-content-between gap-4">
          <div
            class="d-flex gap-2 flex-1 tw-min-w-0"
            :class="isSmScreen ? 'flex-column' : 'flex-row align-items-center '"
          >
            <CopyableValue
              v-bind="copyableSharedProps"
              :value="record.name"
            />

            <CopyableValue
              v-bind="copyableSharedProps"
              :value="record.casrn"
            />

            <CopyableValue
              v-bind="copyableSharedProps"
              :value="record.smiles"
            />

            <CopyableValue
              v-bind="copyableSharedProps"
              :value="record.sid"
            >
              <template #text>
                <b-link
                  :href="`https://comptox.epa.gov/dashboard/dsstoxdb/results?search=${record.sid}`"
                  target="_blank"
                >
                  {{ record.sid }}
                </b-link>
              </template>
            </CopyableValue>
          </div>

          <v-divider
            v-if="isSmScreen"
            vertical
          />

          <div class="d-flex flex-row align-items-center gap-2">
            <v-menu
              v-if="record.smiles"
              open-on-hover
              right
              offset-x
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  :loading="downloading"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>

              <v-list
                dense
                class="py-0 my-0"
              >
                <v-list-item
                  aria-label="Download v2000"
                  @click="onDownload(record, 'v2000')"
                >
                  <v-list-item-title>v2000</v-list-item-title>
                </v-list-item>
                <v-list-item
                  aria-label="Download v3000"
                  @click="onDownload(record, 'v3000')"
                >
                  <v-list-item-title>v3000</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn
              @click="addToCart(record)"
              aria-label="Add to Cart"
              title="Add chemical to the cart"
              icon
            >
              <v-icon>mdi-cart-plus</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import LookupBar from '@/components/lookup/LookupBar.vue'
import { isValidChemical, RESOLVER_URL, SEARCH_URL, download } from '@/main'
import CopyableValue from '@/components/ketcher-editor/CopyableValue.vue'

export default {
  components: { LookupBar, CopyableValue },

  props: {
    value: {
      type: String,
      default: null,
    },
    smiles: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    resolve: {
      type: Boolean,
      default: false,
    },
    lookup: {
      type: Boolean,
      default: false,
    },
    showFound: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      ketcherInstance: null,
      record: null,
      downloading: false,
    }
  },

  computed: {
    // smiles() {
    //   return this.ketcherInstance.getSmiles()
    // },
    molecule: {
      get() {
        return this.ketcherInstance ? this.ketcherInstance.getMolfile() : null
      },
      set(mol) {
        if (this.ketcherInstance) this.ketcherInstance.setMolecule(mol)
      },
    },
    isSmScreen() {
      return this.$vuetify.breakpoint.sm
    },
    copyableSharedProps() {
      return {
        showDivider: !this.isSmScreen,
        tooltipPosition: this.isSmScreen ? 'right' : 'top',
      }
    },
  },

  watch: {
    value(val) {
      if (this.molecule !== val) this.molecule = val
    },
  },

  methods: {
    loadFrame() {
      this.ketcherInstance = this.$refs.ketcherFrame?.contentWindow?.ketcher

      if (!this.ketcherInstance) return

      if (this.value) this.molecule = this.value

      this.ketcherInstance.editor.on('change', this.onStructureChanged)
    },

    onStructureChanged() {
      let self = this
      const ketcherMolfile = self.ketcherInstance.getMolfile()
      const ketcherSmiles = self.ketcherInstance.getSmiles()

      const hasStructure = typeof ketcherSmiles === 'string' && ketcherSmiles.trim().length > 0

      this.$emit('input', !hasStructure ? null : ketcherMolfile)
      this.$emit('update:smiles', ketcherSmiles)

      if (this.lookup || this.showFound) {
        if (ketcherSmiles && ketcherSmiles.trim().length > 0) {
          this.$axios
            .get(`${RESOLVER_URL}/lookup?query=${encodeURIComponent(ketcherSmiles)}&idType=SMILES`)
            .then((response) => {
              if (response && response.data && response.data[0] && isValidChemical(response.data[0])) {
                this.record = response.data[0].chemical
              } else {
                this.record = null
              }
            })
            .catch(() => {
              console.error('Lookup failed for', ketcherSmiles)
            })
        } else {
          this.record = null
        }
        this.$emit('found', this.record)
      }
    },
    applyLookupResult(chemical) {
      this.molecule = chemical.mol
    },
    onDownload(item, format) {
      this.downloading = true
      this.$axios
        .get(`${SEARCH_URL}/download/mol-by-smiles?smiles=${encodeURIComponent(item.smiles)}&format=${format}`)
        .then((response) => {
          download(`${item.cid}_${format}.mol`, response.data)
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          this.downloading = false
        })
    },

    addToCart(record) {
      this.$store.dispatch('addUniqueStructures', [record])
      this.$store.commit('SET_VIEW_STRUCTURES', true)
    },
  },
}
</script>

<style lang="scss" scoped>
.ketcher-frame {
  width: 100%;
  min-height: 550px;
  border: none;
  overflow: hidden;
}

.info {
  margin-top: 0.2em;
  margin-bottom: 1em;
}

.disabled {
  display: block;
  background: black;
  opacity: 0.1;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}
</style>
